/**
 * Breadcrumb Context Manager
 * Tracks collection context when navigating from collection to product pages
 */

class BreadcrumbManager {
    constructor() {
        this.STORAGE_KEY = 'mff_breadcrumb_collection';
        this.init();
    }

    init() {
        // On collection pages: Save collection info when clicking product links
        if (this.isCollectionPage()) {
            this.attachCollectionTracking();
        }

        // On product pages: Display saved collection in breadcrumb
        if (this.isProductPage()) {
            this.updateProductBreadcrumb();
        }

        // Clean up old entries (older than 1 hour)
        this.cleanupOldEntries();
    }

    isCollectionPage() {
        // Check multiple possible template indicators
        const bodyClasses = document.body.className;
        return bodyClasses.includes('template-collection') ||
            bodyClasses.includes('collection-template') ||
            window.location.pathname.includes('/collections/');
    }

    isProductPage() {
        // Check multiple possible template indicators
        const bodyClasses = document.body.className;
        return bodyClasses.includes('template-product') ||
            bodyClasses.includes('product-template') ||
            window.location.pathname.includes('/products/');
    }

    attachCollectionTracking() {
        // Get collection data from current page
        const collectionData = this.getCurrentCollectionData();

        if (!collectionData) return;

        // Track clicks on product links
        document.addEventListener('click', (e) => {
            const productLink = e.target.closest('a[href*="/products/"]');

            if (productLink) {
                const productUrl = productLink.getAttribute('href');

                // Save collection context for this product
                this.saveCollectionContext(productUrl, collectionData);
            }
        }, true); // Use capture phase to ensure we catch it early
    }

    getCurrentCollectionData() {
        // Try to get collection data from meta tags or window object
        const collectionMeta = document.querySelector('meta[property="og:url"]');
        const currentUrl = window.location.pathname;

        // Extract collection handle from URL
        const collectionMatch = currentUrl.match(/\/collections\/([^\/]+)/);

        if (!collectionMatch) return null;

        const handle = collectionMatch[1];

        // Get collection title from page
        let collectionTitle = document.querySelector('h1')?.textContent?.trim() ||
            document.title.split('–')[0]?.trim() ||
            this.formatHandle(handle);

        // Clean up title by removing common prefixes
        collectionTitle = this.cleanCollectionTitle(collectionTitle);

        return {
            handle: handle,
            title: collectionTitle,
            url: `/collections/${handle}`,
            timestamp: Date.now()
        };
    }

    saveCollectionContext(productUrl, collectionData) {
        try {
            // Get existing data
            const stored = sessionStorage.getItem(this.STORAGE_KEY);
            const allData = stored ? JSON.parse(stored) : {};

            // Extract product handle from URL
            const productHandle = productUrl.match(/\/products\/([^\/\?#]+)/)?.[1];

            if (!productHandle) return;

            // Save collection data for this product
            allData[productHandle] = collectionData;

            sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(allData));
        } catch (error) {
            console.warn('Failed to save breadcrumb context:', error);
        }
    }

    getCollectionContext(productHandle) {
        try {
            const stored = sessionStorage.getItem(this.STORAGE_KEY);
            if (!stored) return null;

            const allData = JSON.parse(stored);
            return allData[productHandle] || null;
        } catch (error) {
            console.warn('Failed to retrieve breadcrumb context:', error);
            return null;
        }
    }

    getCurrentProductHandle() {
        const currentUrl = window.location.pathname;
        const productMatch = currentUrl.match(/\/products\/([^\/\?#]+)/);
        return productMatch ? productMatch[1] : null;
    }

    updateProductBreadcrumb() {
        const productHandle = this.getCurrentProductHandle();
        if (!productHandle) return;

        const collectionData = this.getCollectionContext(productHandle);
        if (!collectionData) return;

        // Find the breadcrumb list
        const breadcrumbList = document.querySelector('.breadcrumbs-list');
        if (!breadcrumbList) return;

        // Check if collection breadcrumb already exists (from Liquid)
        const existingCollectionLink = breadcrumbList.querySelector('a[href*="/collections/"]');
        if (existingCollectionLink) {
            // Update existing link if needed
            if (!existingCollectionLink.href.includes(collectionData.handle)) {
                existingCollectionLink.href = collectionData.url;
                existingCollectionLink.textContent = collectionData.title;
            }
            return;
        }

        // Add collection breadcrumb before product
        const productItem = breadcrumbList.querySelector('.breadcrumbs-item:last-child');
        if (!productItem) return;

        const collectionItem = document.createElement('li');
        collectionItem.className = 'breadcrumbs-item';
        collectionItem.innerHTML = `<a class="breadcrumbs-link" style="margin-right: 0.5rem;" href="${collectionData.url}">${this.escapeHtml(collectionData.title)}</a>`;

        productItem.parentNode.insertBefore(collectionItem, productItem);
    }

    cleanupOldEntries() {
        try {
            const stored = sessionStorage.getItem(this.STORAGE_KEY);
            if (!stored) return;

            const allData = JSON.parse(stored);
            const oneHourAgo = Date.now() - (60 * 60 * 1000);
            let hasChanges = false;

            Object.keys(allData).forEach(key => {
                if (allData[key].timestamp < oneHourAgo) {
                    delete allData[key];
                    hasChanges = true;
                }
            });

            if (hasChanges) {
                sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(allData));
            }
        } catch (error) {
            console.warn('Failed to cleanup old breadcrumb entries:', error);
        }
    }

    formatHandle(handle) {
        // Convert handle to title format (e.g., "summer-collection" -> "Summer Collection")
        return handle
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    cleanCollectionTitle(title) {
        // Remove common prefixes like "Collection:", "Collections:", etc.
        const prefixesToRemove = [
            /^Collection:\s*/i,
            /^Collections:\s*/i,
            /^Συλλογή:\s*/i,  // Greek for Collection
            /^Συλλογές:\s*/i   // Greek for Collections
        ];

        let cleanedTitle = title;
        prefixesToRemove.forEach(prefix => {
            cleanedTitle = cleanedTitle.replace(prefix, '');
        });

        return cleanedTitle.trim();
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    // Public method to clear all breadcrumb data
    clear() {
        try {
            sessionStorage.removeItem(this.STORAGE_KEY);
        } catch (error) {
            console.warn('Failed to clear breadcrumb context:', error);
        }
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.breadcrumbManager = new BreadcrumbManager();
    });
} else {
    window.breadcrumbManager = new BreadcrumbManager();
}
